import React, {useEffect, useState} from 'react';
import CompaniesHero from '../../Components/Companies/CompaniesHero';
import Customers from '../../Components/Projects/Customers';
import Employees from '../../Components/Projects/Employees';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {getProjectMembersList, projectMemberRemove, getProject} from '../../../redux/actions/projects/projectsActions';
import './ProjectsParticipants.scss';
import LoadPanel from '../../Components/LoadPanel';
import {patchCompanyEmployeeFire, patchCompanyEmployeeReturn} from '../../../redux/actions/employee/employeeActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import {patchCompanyClientBlock, patchCompanyClientReturn} from '../../../redux/actions/clients/clientsActions';
import NoItems from '../../Components/NoItems';
import {hasPermission} from '../../../helpers/functions';
import {PERMISSIONS} from '../../../helpers/constants';
import {setModal, setModalData, showConfirmPopup} from '../../../redux/actions/app/appActions';
import {mainPath} from '../../../routes/paths';
import {useHistory} from 'react-router-dom';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';

const ProjectsParticipants = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const {profileMe} = useSelector((state) => state.profile);
    const {projectMembersList, projectMembersListLoading, singleProject} = useSelector((state) => state.projects);

    const addPermission = hasPermission(PERMISSIONS.PROJECT_MEMBER_ADD, profileMe?.permissions ?? []);
    const editPermission = hasPermission(PERMISSIONS.PROJECT_MEMBER_EDIT, profileMe?.permissions ?? []);
    const fireEmployeePermission = hasPermission(PERMISSIONS.EMPLOYEE_FIRE, profileMe?.permissions ?? []);
    const returnEmployeePermission = hasPermission(PERMISSIONS.EMPLOYEE_RETURN, profileMe?.permissions ?? []);
    const blockCustomerPermission = hasPermission(PERMISSIONS.CUSTOMER_BLOCK, profileMe?.permissions ?? []);
    const returnCustomerPermission = hasPermission(PERMISSIONS.CUSTOMER_RETURN, profileMe?.permissions ?? []);
    const editEmployeePermission = hasPermission(PERMISSIONS.PROJECT_EMPLOYEE_EDIT, profileMe?.permissions ?? []);
    const removePermission = hasPermission(PERMISSIONS.PROJECT_MEMBER_REMOVE, profileMe?.permissions ?? []);
    const editPerson = hasPermission(PERMISSIONS.USER_FULL_EDIT, profileMe?.permissions ?? []);
    const allowActions = singleProject?.status === 'active' || singleProject?.status === 'pause'
    const isSpectatorOrClient = profileMe?.role === 'user' || profileMe?.role === 'spectator';

    useEffect(() => {
        if (!projectMembersList.length && projectMembersListLoading) {
            dispatch(getProjectMembersList(singleProject.id));
        }
    }, [dispatch, singleProject.id, projectMembersListLoading, projectMembersList]);

    const handleCustomer = async (id) => {
        if(isSpectatorOrClient) {
            return
        }
        history.push(`${mainPath.projectsParticipantsClient.replace(':id', singleProject?.id).replace(':clientId', id)}`);
    };

    const handleEmployees = async (id) => {
        if(isSpectatorOrClient) {
           return
        }
        history.push(`${mainPath.projectsParticipantsEmployee.replace(':id', singleProject?.id).replace(':userId', id)}`);
    };

    const handleAdd = () => {
        history.push(`${mainPath.projectsParticipantsAdd.replace(':id', singleProject?.id)}`);
    };

    const handleBlock = async (id) => {
        setLoading(true);
        try {
            const response = await dispatch(patchCompanyClientBlock(singleProject.companyId, id));
            if (response.payload?.status === 200) {
                await dispatch(getProjectMembersList(singleProject.id));
                notifySuccess(t('all.successfully'));

            }
        } finally {
            setLoading(false);
        }
    };

    const handleFire = async (employeeId) => {
        setLoading(true);
        try {
            const response = await dispatch(patchCompanyEmployeeFire(singleProject.id, employeeId));
            if (response.payload?.status === 202) {
                await dispatch(getProjectMembersList(singleProject.id));
                if (singleProject.id) {
                    await dispatch(getProject(singleProject.id))
                }
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveClient = async (id) => {
        setLoading(true);
        try {
            const response = await dispatch(projectMemberRemove(singleProject.id, id));
            if (response.payload?.status === 204) {
                await Promise.all([dispatch(getProjectMembersList(singleProject.id)), dispatch(getProject(singleProject.id,true))]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveEmployees = async (employeeId) => {
        setLoading(true);
        try {
            const response = await dispatch(projectMemberRemove(singleProject.id, employeeId));
            if (response.payload?.status === 204) {
                await Promise.all([dispatch(getProjectMembersList(singleProject.id)), dispatch(getProject(singleProject.id,true))]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReturnEmployee = async (employeeId) => {
        setLoading(true);
        try {
            const response = await dispatch(patchCompanyEmployeeReturn(singleProject.companyId, employeeId));
            if (response.payload?.status === 202) {
                await dispatch(getProjectMembersList(singleProject.id));
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReturnClient = async (id) => {
        setLoading(true);
        try {
            const response = await dispatch(patchCompanyClientReturn(singleProject.companyId, id));

            if (response.payload?.status === 200) {
                await dispatch(getProjectMembersList(singleProject.id));
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFireRedux = (employeeId) => {
        dispatch(
            showConfirmPopup({
                title: t('participant.fireTitle'),
                text: t('participant.fireText'),
                visible: true,
                confirmCallback: () => handleFire(employeeId)
            })
        );
    };

    const handleRemoveClientRedux = (id) => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('participant.removeTitle'),
                text: t('participant.removeText'),
                confirmCallback: () => handleRemoveClient(id)
            })
        );
    };

    const handleRemoveEmployeesRedux = (employeeId) => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('participant.removeTitle'),
                text: t('participant.removeText'),
                confirmCallback: () => handleRemoveEmployees(employeeId)
            })
        );
    };

    const handleReturnEmployeeRedux = (employeeId) => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('participant.returnTitle'),
                text: t('participant.returnText'),
                confirmCallback: () => handleReturnEmployee(employeeId)
            })
        );
    };

    const handleReturnClientRedux = (id) => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('participant.returnTitle'),
                text: t('participant.returnText'),
                confirmCallback: () => handleReturnClient(id)
            })
        );
    };

    const handleBlockClientRedux = (id) => {
        dispatch(
            showConfirmPopup({
                visible: true,
                title: t('participant.blockTitle'),
                text: t('participant.blockText'),
                confirmCallback: () => handleBlock(id)
            })
        );
    };

    const onEditUser = (employeeId) => {
        dispatch(setModal(POPUPS.EDIT_USER));
        dispatch(setModalData(employeeId));
    };
    if (projectMembersListLoading) {
        return (
            <div className={'companies-page-loader'}>
                <LoadPanel noBg={true} circleSize={50} staticPos={true}/>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={'companies-page-loader'}>
                <LoadPanel noBg={true} circleSize={50} staticPos={true}/>
            </div>
        );
    }

    return (
        <div className='projects-participants-wrapper'>
            <div className='projects-participants'>
                <div className='projects-name'>{singleProject.address}</div>

                <CompaniesHero
                    heading={`${projectMembersList?.clients?.length + projectMembersList?.employees?.length} ${t(
                        'ProjectsParticipants.heading'
                    )}`}
                    subHeading={t('ProjectsParticipants.subHeading')}
                    selectedList={true}
                    handleAdd={handleAdd}
                    disabledActionAdd={!addPermission || !allowActions}
                />
                <>
                    {projectMembersList?.clients?.length >= 1 || projectMembersList?.employees?.length >= 1 ? (
                        <div className='projects-participants__content'>
                            <div className='projects-participants__list-wrapper'>
                                <div className='projects-participants__list'>
                                    {projectMembersList.employees &&
                                        Array.isArray(projectMembersList.employees) &&
                                        projectMembersList.employees.map(
                                            ({
                                                 firstName,
                                                 positionName,
                                                 status,
                                                 avatar,
                                                 employeeId,
                                                 id,
                                                 phone,
                                                 email
                                             }) => (
                                                <Employees
                                                    key={id}
                                                    name={firstName}
                                                    position={positionName}
                                                    status={status}
                                                    id={id}
                                                    avatar={avatar}
                                                    employeeId={employeeId}
                                                    handleId={() => handleEmployees(employeeId)}
                                                    onFire={handleFireRedux}
                                                    onRemoveEmployees={handleRemoveEmployeesRedux}
                                                    onReturn={handleReturnEmployeeRedux}
                                                    editPermission={editEmployeePermission && allowActions}
                                                    returnEmployeePermission={returnEmployeePermission && allowActions}
                                                    fireEmployeePermission={fireEmployeePermission && allowActions}
                                                    removePermission={removePermission && allowActions}
                                                    editPerson={editPerson && allowActions}
                                                    onEditUser={onEditUser}
                                                    email={email}
                                                    phone={phone}
                                                />
                                            )
                                        )}

                                    {projectMembersList.clients &&
                                        Array.isArray(projectMembersList.clients) &&
                                        projectMembersList.clients.map(
                                            ({
                                                 firstName,
                                                 isActive,
                                                 roleName,
                                                 role,
                                                 avatar,
                                                 clientId,
                                                 id,
                                                 phone,
                                                 email
                                             }) => (
                                                <Customers
                                                    key={id}
                                                    firstName={firstName}
                                                    position={role}
                                                    roleName={roleName}
                                                    isActive={isActive}
                                                    id={id}
                                                    avatar={avatar}
                                                    handleId={() => handleCustomer(clientId)}
                                                    onBlock={handleBlockClientRedux}
                                                    onReturn={handleReturnClientRedux}
                                                    onRemoveClient={handleRemoveClientRedux}
                                                    editPermission={editPermission && allowActions}
                                                    removePermission={removePermission && allowActions}
                                                    editPerson={editPerson && allowActions}
                                                    onEditUser={onEditUser}
                                                    email={email}
                                                    phone={phone}
                                                    blockCustomerPermission={blockCustomerPermission && allowActions}
                                                    returnCustomerPermission={returnCustomerPermission && allowActions}
                                                />
                                            )
                                        )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <NoItems
                            description={t('ProjectsParticipants.noParticipantsDescription')}
                            buttonText={t('ProjectsParticipants.noParticipantsButton')}
                            title={t('ProjectsParticipants.noParticipantsTitle')}
                            handleNavigate={handleAdd}
                            center={'center'}
                            disabled={!addPermission || !allowActions}
                        />
                    )}
                </>
            </div>
        </div>
    );
};

export default ProjectsParticipants;
